import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Plus,
  Calendar,
  User,
  Phone,
  Mail,
  MapPin,
  Activity,
  FileText,
  Clock,
  Brain,
  X,
  AlertTriangle,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import medicalDB from "../services/database";
import VisitForm from "./VisitForm";
import openaiService from "../services/openai";
import AIAnalysisRenderer from "./AIAnalysisRenderer";
import PatientFinancialSummary from "./PatientFinancialSummary";
import TransactionForm from "./TransactionForm";

const PatientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVisitForm, setShowVisitForm] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [todaysAppointments, setTodaysAppointments] = useState([]);

  // AI Analysis states
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [loadingRecommendation, setLoadingRecommendation] = useState(false);
  const [showRecommendation, setShowRecommendation] = useState(false);
  const [aiError, setAiError] = useState(null);

  // Q&A states
  const [refinedAnalysis, setRefinedAnalysis] = useState(null);
  const [qaAnswers, setQaAnswers] = useState({});
  const [loadingRefinedAnalysis, setLoadingRefinedAnalysis] = useState(false);

  // Financial states
  const [showTransactionForm, setShowTransactionForm] = useState(false);

  useEffect(() => {
    loadPatient();
    loadAppointments();
  }, [id]);

  const loadPatient = async () => {
    try {
      setLoading(true);
      const data = await medicalDB.getPatient(id);
      if (data) {
        setPatient(data);
      } else {
        navigate("/patients");
      }
    } catch (error) {
      console.error("Error loading patient:", error);
      navigate("/patients");
    } finally {
      setLoading(false);
    }
  };

  const loadAppointments = async () => {
    try {
      const appointmentsData = await medicalDB.getPatientAppointments(id);
      setAppointments(appointmentsData);

      // Filter today's appointments and sort by time proximity
      const today = new Date().toISOString().split("T")[0];
      const todaysAppts = appointmentsData.filter(
        (apt) => apt.date === today && apt.status === "scheduled"
      );

      // Sort by time proximity to current time
      const now = new Date();
      const currentTime = now.getHours() * 60 + now.getMinutes();

      todaysAppts.sort((a, b) => {
        const timeA =
          parseInt(a.startTime.split(":")[0]) * 60 +
          parseInt(a.startTime.split(":")[1]);
        const timeB =
          parseInt(b.startTime.split(":")[0]) * 60 +
          parseInt(b.startTime.split(":")[1]);

        const diffA = Math.abs(timeA - currentTime);
        const diffB = Math.abs(timeB - currentTime);

        return diffA - diffB;
      });

      setTodaysAppointments(todaysAppts);
    } catch (error) {
      console.error("Error loading appointments:", error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Under Observation":
        return "status-under-observation";
      case "Follow-Up":
        return "status-follow-up";
      case "Discharged":
        return "status-discharged";
      default:
        return "bg-gray-500 text-white";
    }
  };

  const getAppointmentTypeName = (type) => {
    const appointmentTypes = {
      ROUTINE: "Routine Checkup",
      FOLLOW_UP: "Follow-up",
      NEW_PATIENT: "New Patient",
      CONSULTATION: "Consultation",
      EMERGENCY: "Emergency",
    };
    return appointmentTypes[type] || type;
  };

  const handleCheckInAppointment = async (appointment) => {
    if (
      window.confirm(
        `Check in ${patient.name} for their ${appointment.startTime} appointment?`
      )
    ) {
      try {
        // Mark appointment as completed
        await medicalDB.updateAppointment(appointment.id, {
          status: "completed",
        });

        // Reload appointments to update the UI
        await loadAppointments();

        // Navigate to visit form with pre-filled data
        navigate(
          `/patients/${id}?newVisit=true&appointmentId=${appointment.id}`
        );
      } catch (error) {
        console.error("Error checking in appointment:", error);
        alert("Failed to check in appointment. Please try again.");
      }
    }
  };

  const handleNewVisit = () => {
    setEditingVisit(null);
    setShowVisitForm(true);
  };

  const handleEditVisit = (visit) => {
    setEditingVisit(visit);
    setShowVisitForm(true);
  };

  const handleVisitSaved = () => {
    setShowVisitForm(false);
    setEditingVisit(null);
    loadPatient(); // Reload patient data
  };

  const handleAIAnalysis = async () => {
    if (!patient || !patient.visits || patient.visits.length === 0) {
      setAiError("AI analysis requires at least one patient visit to analyze.");
      return;
    }

    setLoadingRecommendation(true);
    setAiError(null);

    try {
      const recommendation = await openaiService.generateMedicalRecommendation(
        patient
      );
      setAiRecommendation(recommendation);
      setShowRecommendation(true);
    } catch (error) {
      console.error("AI Analysis Error:", error);
      setAiError(
        error.message ||
          "Failed to generate AI recommendation. Please try again."
      );
    } finally {
      setLoadingRecommendation(false);
    }
  };

  const closeRecommendation = () => {
    setShowRecommendation(false);
    setAiError(null);
    setRefinedAnalysis(null);
    setQaAnswers({});
  };

  const handleSubmitAnswers = async (answers) => {
    if (!aiRecommendation || !aiRecommendation.followUpQuestions) {
      return;
    }

    setLoadingRefinedAnalysis(true);
    setQaAnswers(answers);

    try {
      const refined = await openaiService.generateRefinedAnalysis(
        patient,
        aiRecommendation.followUpQuestions,
        answers
      );
      setRefinedAnalysis(refined);

      // Store Q&A in patient record
      const qaRecord = {
        timestamp: new Date().toISOString(),
        questions: aiRecommendation.followUpQuestions,
        answers: answers,
        refinedAnalysis: refined,
      };

      // Add to patient's AI interactions (you can extend this)
      console.log("Q&A Record for patient storage:", qaRecord);
    } catch (error) {
      console.error("Error generating refined analysis:", error);
      setAiError("Failed to generate refined analysis. Please try again.");
    } finally {
      setLoadingRefinedAnalysis(false);
    }
  };

  // Financial handlers
  const handleRecordPayment = () => {
    setShowTransactionForm(true);
  };

  const handleTransactionSaved = () => {
    setShowTransactionForm(false);
    // The PatientFinancialSummary component will automatically refresh
    // when it detects changes in the database
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading patient...</div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Patient not found
        </h3>
        <Link to="/patients" className="btn-primary">
          Back to Patients
        </Link>
      </div>
    );
  }

  if (showVisitForm) {
    return (
      <VisitForm
        patient={patient}
        visit={editingVisit}
        onSave={handleVisitSaved}
        onCancel={() => setShowVisitForm(false)}
      />
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/patients")}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{patient.name}</h1>
            <p className="text-gray-600">Patient Details</p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {/* Check-in button for today's appointments */}
          {todaysAppointments.length > 0 && (
            <button
              onClick={() => handleCheckInAppointment(todaysAppointments[0])}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg flex items-center space-x-2 transition-colors h-10"
              title={`Check in for ${todaysAppointments[0].startTime} appointment`}
            >
              <User size={20} />
              <span>Check In ({todaysAppointments[0].startTime})</span>
            </button>
          )}

          <button
            data-agent-action="add-visit"
            onClick={handleNewVisit}
            className="bg-medical-blue hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg flex items-center space-x-2 transition-colors h-10"
          >
            <Plus size={20} />
            <span>New Visit</span>
          </button>
          <Link
            to={`/appointments/new?patientId=${id}`}
            className="bg-medical-blue hover:bg-blue-700 text-white px-4 py-6 rounded-lg flex items-center space-x-2 transition-colors h-10"
            data-agent-action="schedule-appointment"
          >
            <Calendar size={20} />
            <span>Schedule Appointment</span>
          </Link>
          {/* AI Analysis Button */}
          {patient?.visits && patient.visits.length > 0 && (
            <button
              onClick={handleAIAnalysis}
              data-agent-action="generate-analysis"
              disabled={loadingRecommendation}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              {loadingRecommendation ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Brain size={20} />
              )}
              <span>
                {loadingRecommendation ? "Analyzing..." : "AI Analysis"}
              </span>
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Patient Overview */}
        <div className="lg:col-span-1 space-y-6">
          {/* Basic Info */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-16 h-16 bg-medical-lightblue rounded-full flex items-center justify-center">
                  <User className="h-8 w-8 text-medical-blue" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {patient.name}
                  </h2>
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                      patient.status
                    )}`}
                  >
                    {patient.status}
                  </span>
                </div>
              </div>

              {/* Edit Button */}
              <Link
                to={`/patients/${id}/edit`}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-600 hover:text-medical-blue"
                title="Edit Patient"
                data-testid="edit-patient-button"
              >
                <Edit size={20} />
              </Link>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Age:</span>
                <span className="font-medium">{patient.age} years</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Gender:</span>
                <span className="font-medium">{patient.gender}</span>
              </div>
              {patient.bloodType && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Blood Type:</span>
                  <span className="font-medium">{patient.bloodType}</span>
                </div>
              )}
              {patient.phone && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone:</span>
                  <span className="font-medium">{patient.phone}</span>
                </div>
              )}
              {patient.location && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Location:</span>
                  <span className="font-medium">{patient.location}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Total Visits:</span>
                <span className="font-medium">
                  {patient.visits?.length || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Registered:</span>
                <span className="font-medium">
                  {format(new Date(patient.createdAt), "MMM dd, yyyy")}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Last Updated:</span>
                <span className="font-medium">
                  {format(new Date(patient.updatedAt), "MMM dd, yyyy")}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <Activity className="mr-2" size={20} />
              Recent Activity
            </h3>
            {patient.visits && patient.visits.length > 0 ? (
              <div className="space-y-2">
                <div className="text-sm">
                  <span className="text-gray-600">Last Visit:</span>
                  <span className="ml-2 font-medium">
                    {format(new Date(patient.visits[0].date), "MMM dd, yyyy")}
                  </span>
                  <span className="ml-2 text-xs text-gray-400">
                    (
                    {formatDistanceToNow(new Date(patient.visits[0].date), {
                      addSuffix: true,
                    })}
                    )
                  </span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-600">Recent Complaints:</span>
                  <div className="mt-1">
                    {patient.visits[0].complaints
                      ?.slice(0, 3)
                      .map((complaint, index) => (
                        <span
                          key={index}
                          className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs mr-1 mb-1"
                        >
                          {complaint}
                        </span>
                      )) || "None recorded"}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-gray-600 text-sm">No visits recorded yet</p>
            )}
          </div>

          {/* Appointments */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 flex items-center">
                <Calendar className="mr-2" size={20} />
                Appointments
              </h3>
              <Link
                to={`/appointments/new?patientId=${id}`}
                className="text-sm text-medical-blue hover:underline"
              >
                Schedule New
              </Link>
            </div>

            {appointments.length > 0 ? (
              <div className="space-y-3">
                {appointments.slice(0, 3).map((appointment) => {
                  const appointmentDate = new Date(appointment.date);
                  const today = new Date();
                  const isUpcoming = appointmentDate >= today;
                  const isToday =
                    appointment.date === today.toISOString().split("T")[0];
                  const canCheckIn =
                    isToday && appointment.status === "scheduled";

                  return (
                    <div
                      key={appointment.id}
                      className={`p-3 rounded-lg border ${
                        isUpcoming
                          ? "bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800"
                          : "bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Clock size={16} className="text-gray-500" />
                          <span className="text-sm font-medium">
                            {format(appointmentDate, "MMM dd, yyyy")} at{" "}
                            {appointment.startTime}
                          </span>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              appointment.status === "scheduled"
                                ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                                : appointment.status === "completed"
                                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                            }`}
                          >
                            {appointment.status}
                          </span>
                        </div>

                        {/* Check-in button for today's scheduled appointments */}
                        {canCheckIn && (
                          <button
                            onClick={() =>
                              handleCheckInAppointment(appointment)
                            }
                            className="ml-2 px-2 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                            title="Check in patient"
                          >
                            Check In
                          </button>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                        {getAppointmentTypeName(appointment.type)}
                        {appointment.notes && ` • ${appointment.notes}`}
                      </div>
                    </div>
                  );
                })}

                {appointments.length > 3 && (
                  <Link
                    to="/appointments"
                    className="block text-center text-sm text-medical-blue hover:underline py-2"
                  >
                    View all {appointments.length} appointments
                  </Link>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <Calendar className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                <p className="text-gray-600 text-sm mb-3">
                  No appointments scheduled
                </p>
                <Link
                  to={`/appointments/new?patientId=${id}`}
                  className="text-sm text-medical-blue hover:underline"
                >
                  Schedule first appointment
                </Link>
              </div>
            )}
          </div>

          {/* Financial Summary */}
          <PatientFinancialSummary
            patientId={id}
            onRecordPayment={handleRecordPayment}
          />
        </div>

        {/* Visits Timeline */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Calendar className="mr-2" size={24} />
                Visit History
              </h2>
              <span className="text-sm text-gray-600">
                {patient.visits?.length || 0} total visits
              </span>
            </div>

            {patient.visits && patient.visits.length > 0 ? (
              <div className="space-y-6">
                {patient.visits
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((visit, index) => (
                    <div key={visit.id} className="relative">
                      {/* Timeline line */}
                      {index < patient.visits.length - 1 && (
                        <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-gray-200"></div>
                      )}

                      {/* Visit card */}
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-medical-blue rounded-full flex items-center justify-center">
                          <FileText className="h-6 w-6 text-white" />
                        </div>

                        <div className="flex-1 bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <Clock size={16} className="text-gray-500" />
                              <span className="font-medium text-gray-900">
                                {format(new Date(visit.date), "MMMM dd, yyyy")}
                              </span>
                              <span className="text-gray-500">
                                {format(new Date(visit.date), "h:mm a")}
                              </span>
                              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
                                {formatDistanceToNow(new Date(visit.date), {
                                  addSuffix: true,
                                })}
                              </span>
                            </div>
                            <button
                              onClick={() => handleEditVisit(visit)}
                              className="text-gray-400 hover:text-medical-blue"
                            >
                              <Edit size={16} />
                            </button>
                          </div>

                          {/* Complaints */}
                          {visit.complaints && visit.complaints.length > 0 && (
                            <div className="mb-3">
                              <h4 className="text-sm font-medium text-gray-700 mb-1">
                                Chief Complaints:
                              </h4>
                              <div className="flex flex-wrap gap-1">
                                {visit.complaints.map((complaint, idx) => (
                                  <span
                                    key={idx}
                                    className="bg-medical-lightblue text-medical-blue px-2 py-1 rounded text-sm"
                                  >
                                    {complaint}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Vital Signs */}
                          {visit.vitalSigns &&
                            Object.keys(visit.vitalSigns).length > 0 && (
                              <div className="mb-3">
                                <h4 className="text-sm font-medium text-gray-700 mb-1">
                                  Vital Signs:
                                </h4>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                                  {visit.vitalSigns.temp && (
                                    <span>Temp: {visit.vitalSigns.temp}°C</span>
                                  )}
                                  {visit.vitalSigns.bp && (
                                    <span>BP: {visit.vitalSigns.bp}</span>
                                  )}
                                  {visit.vitalSigns.hr && (
                                    <span>HR: {visit.vitalSigns.hr} bpm</span>
                                  )}
                                  {visit.vitalSigns.spo2 && (
                                    <span>SpO2: {visit.vitalSigns.spo2}%</span>
                                  )}
                                  {visit.vitalSigns.weight && (
                                    <span>
                                      Weight: {visit.vitalSigns.weight} kg
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}

                          {/* Notes */}
                          {visit.notes && (
                            <div className="mb-3">
                              <h4 className="text-sm font-medium text-gray-700 mb-1">
                                Notes:
                              </h4>
                              <p className="text-sm text-gray-600">
                                {visit.notes}
                              </p>
                            </div>
                          )}

                          {/* Prescription */}
                          {visit.prescription &&
                            visit.prescription.length > 0 && (
                              <div className="mb-3">
                                <h4 className="text-sm font-medium text-gray-700 mb-1">
                                  Prescribed:
                                </h4>
                                <div className="space-y-1">
                                  {visit.prescription.map((drug, idx) => (
                                    <div
                                      key={idx}
                                      className="text-sm text-gray-600"
                                    >
                                      {drug.name} - {drug.dose}
                                      {drug.unit}
                                      {drug.duration && ` for ${drug.duration}`}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                          {/* Administered Medications */}
                          {visit.administeredMeds &&
                            visit.administeredMeds.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-1">
                                  Administered:
                                </h4>
                                <div className="space-y-1">
                                  {visit.administeredMeds.map((med, idx) => (
                                    <div
                                      key={idx}
                                      className="text-sm text-gray-600 flex items-center space-x-2"
                                    >
                                      <span>
                                        {med.name} - {med.dose}
                                        {med.unit} ({med.route})
                                      </span>
                                      {med.time && (
                                        <span className="text-xs text-gray-500 bg-gray-100 px-1 rounded">
                                          {med.time}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No visits recorded
                </h3>
                <p className="text-gray-600 mb-6">
                  Start documenting this patient's medical history
                </p>
                <button onClick={handleNewVisit} className="btn-primary">
                  Record First Visit
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI Recommendation Modal */}
      {showRecommendation && aiRecommendation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
                    <Brain className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      AI Clinical Analysis
                    </h3>
                    <p className="text-sm text-gray-600">
                      Medical insights for {patient.name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeRecommendation}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Disclaimer */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">
                      Clinical Decision Support Tool
                    </p>
                    <p>
                      These AI-generated insights are for informational purposes
                      only and must be reviewed and validated by a licensed
                      healthcare professional. This tool does not replace
                      clinical judgment.
                    </p>
                  </div>
                </div>
              </div>

              {/* AI Recommendation Content */}
              <div className="mb-4 max-h-[60vh] overflow-y-auto">
                <AIAnalysisRenderer
                  analysis={aiRecommendation}
                  patientName={patient.name}
                  onSubmitAnswers={handleSubmitAnswers}
                  refinedAnalysis={refinedAnalysis}
                  qaAnswers={qaAnswers}
                />
              </div>

              {/* Loading indicator for refined analysis */}
              {loadingRefinedAnalysis && (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                    <span className="text-gray-600">
                      Generating refined analysis...
                    </span>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="flex justify-between items-center text-xs text-gray-500 pt-4 border-t border-gray-200">
                <span>Analysis generated: {new Date().toLocaleString()}</span>
                <span>
                  Based on {patient.visits?.length} visit
                  {patient.visits?.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {aiError && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    AI Analysis Error
                  </h3>
                </div>
              </div>

              <p className="text-gray-600 mb-6">{aiError}</p>

              <div className="flex justify-end">
                <button
                  onClick={() => setAiError(null)}
                  className="btn-primary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Form Modal */}
      <TransactionForm
        isOpen={showTransactionForm}
        onClose={() => setShowTransactionForm(false)}
        onSave={handleTransactionSaved}
        patientId={id}
      />
    </div>
  );
};

export default PatientDetail;
